// Renderer + scene + lighting setup shared by the game and the lab pages.
// One low golden sun (from behind-left so silhouettes rim-light) + a gold/teal hemisphere.

import * as THREE from 'three';
import { HAZE } from './materials';
import { PAL } from './palette';
import { PostPipeline } from './post';

export interface Stage {
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  sun: THREE.DirectionalLight;
  hemi: THREE.HemisphereLight;
  post: PostPipeline;
  resize(): void;
  /** keep the shadow frustum on the action */
  followShadow(target: THREE.Vector3): void;
}

export function createStage(canvas: HTMLCanvasElement, opts: { quality?: number; pixelRatioCap?: number } = {}): Stage {
  const quality = opts.quality ?? 2;
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: false, powerPreference: 'high-performance', stencil: false });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, opts.pixelRatioCap ?? 1.5));
  renderer.outputColorSpace = THREE.LinearSRGBColorSpace; // post pass does the sRGB encode
  renderer.toneMapping = THREE.NoToneMapping;
  renderer.shadowMap.enabled = quality > 0;
  renderer.shadowMap.type = THREE.PCFShadowMap;
  renderer.setClearColor(PAL.hazeGold);
  renderer.info.autoReset = false; // the post pass renders twice; reset once per frame

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(70, 1, 0.1, 1400);

  const sunDir = HAZE.uSunDir.value as THREE.Vector3;
  const sun = new THREE.DirectionalLight(new THREE.Color(PAL.sunCore), 2.6);
  sun.position.copy(sunDir).multiplyScalar(120);
  sun.castShadow = quality > 0;
  const sm = quality >= 3 ? 4096 : quality >= 2 ? 2048 : 1024;
  sun.shadow.mapSize.set(sm, sm);
  const sc = sun.shadow.camera as THREE.OrthographicCamera;
  sc.left = -55; sc.right = 55; sc.top = 55; sc.bottom = -55; sc.near = 1; sc.far = 320;
  sun.shadow.bias = -0.0006;
  sun.shadow.normalBias = 0.04;
  scene.add(sun, sun.target);

  // sky gold over ground teal: shadows go teal, never grey
  const hemi = new THREE.HemisphereLight(new THREE.Color(0xffd9a0), new THREE.Color(PAL.shadowTeal), 1.25);
  scene.add(hemi);

  const post = new PostPipeline(renderer, quality >= 1 ? 4 : 0);
  post.sunDir = sunDir;
  // ?bloom=<n> overrides the HDR bloom strength (0 = off) for side-by-side checks
  const bq = typeof location !== 'undefined' ? new URLSearchParams(location.search).get('bloom') : null;
  if (bq !== null) post.bloom = Number(bq);

  const resize = () => {
    const w = canvas.clientWidth || window.innerWidth, h = canvas.clientHeight || window.innerHeight;
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    const s = renderer.getDrawingBufferSize(new THREE.Vector2());
    post.setSize(s.x, s.y);
  };
  resize();

  const followShadow = (t: THREE.Vector3) => {
    // snap to texel grid to stop shadow shimmer
    const step = (sc.right - sc.left) / sun.shadow.mapSize.x;
    const x = Math.round(t.x / step) * step, z = Math.round(t.z / step) * step;
    sun.target.position.set(x, t.y, z);
    sun.position.set(x, t.y, z).addScaledVector(sunDir, 140);
  };

  return { renderer, scene, camera, sun, hemi, post, resize, followShadow };
}
